// @flow

import React, { useCallback, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';

import { Dialog } from '../../base/dialog';
import { Icon, IconParticipants } from '../../base/icons';
import { ParticipantItem } from '../../participants-pane/components/ParticipantItem';
import { ACTION_TRIGGER, MEDIA_STATE } from '../../participants-pane/constants';
import theme from '../../participants-pane/theme.json';
import { setPrejoinVisitorsMode } from '../../visitors/actions';
import {
    WEBINAR_HIDDEN_DEFAULT_AUDIENCE,
    canJoinHidden,
    getJoinHiddenAudience,
    isBackstageModeEnabled,
    setJoinHidden,
    setStartBackstage
} from '../../webinar';

/**
 * Tabs of the dialog. Setup first: the decisions there are the ones that cannot
 * be made later, so they are what the dialog opens on. The rosters follow.
 */
const TAB_SETUP = 'setup';
const TAB_IN_CALL = 'inCall';
const TAB_WAITING = 'waiting';

type Props = {

    /**
     * People already in the meeting, as reported by the server.
     */
    participantList: Array<Object>,

    /**
     * Backend-safe room name, needed to arm visitor mode before joining.
     */
    roomName: string,

    /**
     * People waiting to be let in, as reported by the server.
     */
    waitingList: Array<Object>
};

/**
 * The moderator's pre-join control panel, as a dialog.
 *
 * Uses prejoin card styling and the shared modal for focus, dismissal and
 * scrolling. People retain the participants pane's existing row component.
 *
 * It carries the decisions that have to be made before joining:
 *
 *  * where this moderator lands (backstage) and whether the room sees them;
 *  * whether the meeting runs in visitor mode. That one genuinely cannot wait:
 *    jicofo routes a participant when it answers their conference request, so an
 *    audience arriving before the host is routed by whatever the room said at
 *    that moment;
 *  * who is already in the meeting, and who is waiting to be let in - both
 *    pushed by the server, so the lists move while the moderator watches them.
 *
 * The people lists are populated only when Prosody verified this session's JWT
 * as a moderator, so they are absent - not empty - for everybody else, and the
 * tabs that show them are hidden accordingly.
 *
 * @param {Props} props - Component props.
 * @returns {React$Node}
 */
export default function ModeratorPrejoinDialog({ participantList = [], roomName, waitingList = [] }: Props) {
    const { t } = useTranslation();
    const tabId = useId();
    const dispatch = useDispatch();

    // Prejoin has no MUC role yet, so moderator-ness is the token's claim.
    const isTokenModerator = useSelector(state => Boolean(
        state['features/base/jwt']?.user?.moderator || state['features/base/jwt']?.moderator));
    const showJoinHidden = useSelector(canJoinHidden);
    const backstageEnabled = useSelector(isBackstageModeEnabled);
    const showStartBackstage = isTokenModerator && backstageEnabled;
    const startBackstage = useSelector(state => Boolean(state['features/webinar']?.startBackstage));
    const joinHiddenAudience = useSelector(getJoinHiddenAudience);
    const visitorsEnabled = useSelector(state => Boolean(state['features/visitors']?.prejoinEnabled));
    const visitorsPending = useSelector(state => Boolean(state['features/visitors']?.prejoinPending));

    const [ activeTab, setActiveTab ] = useState(TAB_SETUP);

    const tabs = [
        { id: TAB_SETUP,
            label: t('prejoin.paneMeetingSetup') },
        ...isTokenModerator ? [
            { count: participantList.length,
                id: TAB_IN_CALL,
                label: t('prejoin.peopleInTheCall') },
            { count: waitingList.length,
                id: TAB_WAITING,
                label: t('prejoin.paneWaitingToJoin') }
        ] : []
    ];

    const onTabKeyDown = event => {
        const buttons = Array.from(event.currentTarget.querySelectorAll('[role="tab"]'));
        const index = buttons.indexOf(document.activeElement);
        let nextIndex;

        switch (event.key) {
        case 'ArrowRight':
            nextIndex = (index + 1) % buttons.length;
            break;
        case 'ArrowLeft':
            nextIndex = (index - 1 + buttons.length) % buttons.length;
            break;
        case 'Home':
            nextIndex = 0;
            break;
        case 'End':
            nextIndex = buttons.length - 1;
            break;
        default:
            return;
        }

        event.preventDefault();
        buttons[nextIndex].focus();
        setActiveTab(tabs[nextIndex].id);
    };

    const onToggleBackstage = useCallback(
        e => dispatch(setStartBackstage(e.target.checked)),
        [ dispatch ]);

    // The audience, not a boolean: unchecking clears it, and a future UI can
    // pass a wider role list here without changing anything downstream.
    const onToggleJoinHidden = useCallback(
        e => dispatch(setJoinHidden(e.target.checked ? WEBINAR_HIDDEN_DEFAULT_AUDIENCE : null)),
        [ dispatch ]);

    const onToggleVisitors = useCallback(
        e => dispatch(setPrejoinVisitorsMode(roomName, e.target.checked)),
        [ dispatch, roomName ]);

    /**
     * Draws one list of people with the pane's own row component.
     *
     * The entries come from the server rather than from redux - these people are
     * in a room this client has not joined - so the row is fed explicitly and
     * its per-person menu is turned off: there is nothing to moderate from
     * outside the meeting.
     *
     * @param {Array<Object>} people - The people to list.
     * @param {string} emptyText - Shown when the list is empty.
     * @returns {React$Node}
     */
    function renderPeople(people, emptyText) {
        if (!people.length) {
            return (
                <div className = 'prejoin-moderator-empty'>
                    <span
                        aria-hidden = 'true'
                        className = 'prejoin-moderator-empty-icon'>
                        <Icon
                            color = 'currentColor'
                            size = { 28 }
                            src = { IconParticipants } />
                    </span>
                    <p>{ emptyText }</p>
                </div>
            );
        }

        return people.map((person, i) => (
            <ParticipantItem
                actionsTrigger = { ACTION_TRIGGER.PERMANENT }
                audioMediaState = { MEDIA_STATE.NONE }
                displayName = { person.name || person.id }
                hideOptions = { true }
                index = { i + 1 }
                isModerator = { person.role === 'moderator' }
                key = { person.id }
                participant = { person }
                participantList = { people }
                videoMuteState = { MEDIA_STATE.NONE } />
        ));
    }

    /**
     * Draws a setting with its description and native checkbox toggle.
     *
     * @param {Object} options - Title, hint, checked/disabled state and handler.
     * @returns {React$Node}
     */
    function renderOption({ checked, disabled, hint, onChange, title }) {
        return (
            <label className = 'prejoin-moderator-option'>
                <span className = 'prejoin-moderator-option-text'>
                    <span className = 'prejoin-moderator-option-title'>{ title }</span>
                    <span className = 'prejoin-moderator-option-hint'>{ hint }</span>
                </span>
                <input
                    checked = { checked }
                    className = 'prejoin-moderator-switch'
                    disabled = { disabled }
                    onChange = { onChange }
                    type = 'checkbox' />
            </label>
        );
    }

    return (
        <Dialog
            dialogClassName = 'moderator-prejoin-dialog'
            disableEnter = { true }
            hideCancelButton = { true }
            okKey = 'dialog.done'
            titleKey = 'prejoin.moderatorTools'
            width = 'medium'>
            <ThemeProvider theme = { theme }>
                <div className = 'prejoin_moderator_dialog-content'>
                    <p className = 'prejoin-moderator-intro'>
                        { t('prejoin.moderatorToolsHint') }
                    </p>
                    <div
                        aria-label = { t('prejoin.moderatorTools') }
                        className = 'prejoin-moderator-tabs'
                        onKeyDown = { onTabKeyDown }
                        role = 'tablist'>
                        { tabs.map(tab => (
                            <button
                                aria-controls = { `${tabId}-panel` }
                                aria-selected = { activeTab === tab.id }
                                id = { `${tabId}-${tab.id}` }
                                key = { tab.id }
                                onClick = { () => setActiveTab(tab.id) }
                                role = 'tab'
                                tabIndex = { activeTab === tab.id ? 0 : -1 }
                                type = 'button'>
                                <span>{ tab.label }</span>
                                { typeof tab.count === 'number'
                                    && <span className = 'prejoin-moderator-tab-count'>{ tab.count }</span>
                                }
                            </button>
                        )) }
                    </div>
                    <div
                        aria-labelledby = { `${tabId}-${activeTab}` }
                        className = 'prejoin-moderator-panel'
                        id = { `${tabId}-panel` }
                        role = 'tabpanel'
                        tabIndex = { 0 }>
                        { activeTab === TAB_SETUP && (
                            <>
                                { (showStartBackstage || showJoinHidden) && (
                                    <div className = 'prejoin-moderator-card'>
                                        <div className = 'prejoin-moderator-card-header'>
                                            <span className = 'prejoin-moderator-card-title'>
                                                { t('prejoin.paneJoinOptions') }
                                            </span>
                                        </div>
                                        { showStartBackstage && renderOption({
                                            checked: startBackstage,
                                            hint: t('webinar.startBackstageHint'),
                                            onChange: onToggleBackstage,
                                            title: t('webinar.startBackstage')
                                        }) }
                                        { showJoinHidden && renderOption({
                                            checked: Boolean(joinHiddenAudience),
                                            hint: t('webinar.joinHiddenHint'),
                                            onChange: onToggleJoinHidden,
                                            title: t('webinar.joinHidden')
                                        }) }
                                    </div>
                                ) }
                                { isTokenModerator && (
                                    <div className = 'prejoin-moderator-card'>
                                        { renderOption({
                                            checked: visitorsEnabled,
                                            disabled: visitorsPending,
                                            hint: t('prejoin.visitorModeHint'),
                                            onChange: onToggleVisitors,
                                            title: t('prejoin.visitorMode')
                                        }) }
                                    </div>
                                ) }
                            </>
                        ) }

                        { activeTab === TAB_IN_CALL && isTokenModerator
                        && renderPeople(participantList, t('prejoin.nobodyInTheCall')) }

                        { activeTab === TAB_WAITING && isTokenModerator && (
                            <>
                                <span className = 'prejoin-moderator-hint'>
                                    { t('prejoin.paneWaitingHint') }
                                </span>
                                { renderPeople(waitingList, t('prejoin.nobodyWaiting')) }
                            </>
                        ) }
                    </div>
                </div>
            </ThemeProvider>
        </Dialog>
    );
}
